import { router } from "@inertiajs/react"
import {
  BookOpen,
  Camera,
  Lightbulb,
  Loader2,
  Upload,
} from "lucide-react"
import { useCallback, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ""
  )
}

/** Resize image client-side to keep uploads fast & Gemini costs low. */
function resizeImage(file: File, maxDim = 2048): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height / width) * maxDim)
          width = maxDim
        } else {
          width = Math.round((width / height) * maxDim)
          height = maxDim
        }
      }
      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      canvas.getContext("2d")!.drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Resize failed"))),
        "image/jpeg",
        0.85,
      )
    }
    img.onerror = () => reject(new Error("Failed to load image"))
    img.src = URL.createObjectURL(file)
  })
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SelfStudyNew() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [label, setLabel] = useState("")
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ---- file handling ----

  const handleFile = useCallback((selected: File) => {
    if (!selected.type.startsWith("image/")) {
      setError("Please select an image file (JPEG, PNG, etc.)")
      return
    }
    if (selected.size > 10 * 1024 * 1024) {
      setError("Image must be under 10 MB")
      return
    }
    setError(null)
    setFile(selected)
    setPreview(URL.createObjectURL(selected))
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) handleFile(selected)
  }

  // ---- drag & drop ----

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) handleFile(dropped)
  }

  // ---- submit ----

  const handleSubmit = async () => {
    if (!file) return
    setIsProcessing(true)
    setError(null)

    try {
      const resized = await resizeImage(file)
      const formData = new FormData()
      formData.append("image", resized, "study-page.jpg")
      if (label.trim()) formData.append("label", label.trim())

      const res = await fetch("/self-study", {
        method: "POST",
        headers: { "X-CSRF-Token": getCsrfToken() },
        body: formData,
      })

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string
        } | null
        throw new Error(body?.error ?? `Upload failed (${res.status})`)
      }

      const data = (await res.json()) as {
        study_import: { uuid: string }
      }
      router.visit(`/self-study/${data.study_import.uuid}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
      setIsProcessing(false)
    }
  }

  // ---- rendering ----

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Dashboard", href: "/dashboard" },
        { title: "Self Study", href: "/self-study/new" },
      ]}
    >
      <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
        {/* ── Header ── */}
        <div
          className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both space-y-2"
          style={{ animationDelay: "50ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-amber-100 p-2.5 dark:bg-amber-900/30">
              <BookOpen className="size-5 text-amber-700 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Scan Study Material
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Photograph a textbook page to extract vocabulary
              </p>
            </div>
          </div>
        </div>

        {/* ── Processing State ── */}
        {isProcessing ? (
          <Card
            className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both overflow-hidden"
            style={{ animationDelay: "100ms" }}
          >
            <CardContent className="space-y-6 p-6">
              {/* Preview thumbnail */}
              {preview && (
                <div className="flex items-center gap-4">
                  <img
                    src={preview}
                    alt="Uploaded page"
                    className="size-16 rounded-lg border object-cover shadow-sm"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                      <Loader2 className="size-4 animate-spin text-amber-600" />
                      AI is reading your page...
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
                      Extracting English–Thai vocabulary pairs
                    </p>
                  </div>
                </div>
              )}

              {/* Skeleton table */}
              <div className="space-y-0 rounded-lg border border-slate-200 dark:border-slate-700">
                {/* Header skeleton */}
                <div className="flex gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
                  <Skeleton className="h-4 w-20" />
                  <div className="flex-1" />
                  <Skeleton className="h-4 w-16" />
                </div>
                {/* Row skeletons with staggered animation */}
                {[
                  [100, 70],
                  [80, 55],
                  [120, 65],
                  [90, 80],
                  [110, 50],
                  [95, 60],
                ].map(([left, right], i) => (
                  <div
                    key={i}
                    className="animate-in fade-in fill-mode-both flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 dark:border-slate-800"
                    style={{ animationDelay: `${300 + i * 120}ms` }}
                  >
                    <Skeleton className="size-4 rounded" />
                    <Skeleton className="h-4" style={{ width: left }} />
                    <div className="flex-1" />
                    <Skeleton className="h-4" style={{ width: right }} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ── Upload Zone ── */}
            <Card
              className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both overflow-hidden"
              style={{ animationDelay: "100ms" }}
            >
              <CardContent className="p-0">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleInputChange}
                  className="hidden"
                />

                {preview ? (
                  /* ── Image Preview ── */
                  <div className="space-y-4 p-6">
                    <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm dark:border-slate-700">
                      <img
                        src={preview}
                        alt="Selected study material"
                        className="h-64 w-full object-contain bg-slate-50 dark:bg-slate-800/50"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute right-3 top-3 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white dark:bg-slate-800/90 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        <Camera className="mr-1.5 inline size-3.5" />
                        Retake
                      </button>
                    </div>

                    {/* Optional label */}
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                        Session Label{" "}
                        <span className="text-slate-400">(optional)</span>
                      </label>
                      <Input
                        type="text"
                        placeholder='e.g. "Thai for Beginners Ch. 3"'
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  /* ── Drop Zone ── */
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                      "group w-full cursor-pointer p-8 text-center transition-all duration-300",
                      isDragging
                        ? "bg-amber-50 dark:bg-amber-950/20"
                        : "bg-gradient-to-b from-amber-50/50 to-transparent hover:from-amber-50 dark:from-amber-950/10 dark:hover:from-amber-950/20",
                    )}
                  >
                    <div
                      className={cn(
                        "mx-auto flex flex-col items-center rounded-2xl border-2 border-dashed p-10 transition-all duration-300",
                        isDragging
                          ? "scale-[1.02] border-amber-400 bg-amber-100/50 dark:border-amber-500 dark:bg-amber-900/20"
                          : "border-slate-300 group-hover:border-amber-400 dark:border-slate-600 dark:group-hover:border-amber-500",
                      )}
                    >
                      <div
                        className={cn(
                          "mb-4 rounded-2xl p-4 transition-all duration-300",
                          isDragging
                            ? "bg-amber-200 dark:bg-amber-800/40"
                            : "bg-amber-100 group-hover:bg-amber-200 dark:bg-amber-900/30 dark:group-hover:bg-amber-800/40",
                        )}
                      >
                        <Camera
                          className={cn(
                            "size-8 transition-all duration-300",
                            isDragging
                              ? "scale-110 text-amber-700 dark:text-amber-300"
                              : "text-amber-600 group-hover:scale-110 dark:text-amber-400",
                          )}
                        />
                      </div>

                      <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                        Tap to photograph your study material
                      </p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                        <span className="hidden sm:inline">
                          or drag &amp; drop an image here &middot;{" "}
                        </span>
                        JPEG, PNG up to 10 MB
                      </p>
                    </div>
                  </button>
                )}
              </CardContent>
            </Card>

            {/* ── Tips ── */}
            <div
              className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
              style={{ animationDelay: "200ms" }}
            >
              <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50/50 p-4 dark:border-amber-800/30 dark:bg-amber-950/10">
                <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                  <p className="font-medium text-slate-700 dark:text-slate-300">
                    Tips for best results
                  </p>
                  <ul className="list-inside list-disc space-y-0.5 text-xs">
                    <li>Lay the book flat with good lighting</li>
                    <li>Capture one page at a time</li>
                    <li>
                      Works best with vocabulary lists, glossaries &amp;
                      word tables
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ── Error ── */}
            {error && (
              <div className="animate-in fade-in rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/40 dark:bg-red-950/20 dark:text-red-400">
                {error}
              </div>
            )}

            {/* ── Submit ── */}
            {file && (
              <div
                className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
                style={{ animationDelay: "100ms" }}
              >
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={isProcessing}
                  className="w-full gap-2"
                  size="lg"
                >
                  <Upload className="size-4" />
                  Extract Vocabulary
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}
